import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, X, Send, Loader2 } from 'lucide-react';
import { useLang } from '@/context/LanguageContext';
import { streamChat } from '@/lib/api';

export default function AIAssistantWidget() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const openAI = () => setOpen(true);
    window.addEventListener('itw:open-ai', openAI);
    return () => window.removeEventListener('itw:open-ai', openAI);
  }, []);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: 'assistant', content: t('ai.greeting') }]);
    }
  }, [open]); // eslint-disable-line

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    setStreaming(true);
    await streamChat({
      sessionId,
      message: text,
      language: lang,
      onSession: (id) => setSessionId(id),
      onDelta: (delta) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: 'assistant', content: copy[copy.length - 1].content + delta };
          return copy;
        });
      },
      onError: (err) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: 'assistant', content: err, error: true };
          return copy;
        });
      },
      onDone: () => setStreaming(false),
    });
  };

  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <>
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0, opacity: 0 }}
            onClick={() => setOpen(true)} data-testid="ai-assistant-toggle-button"
            className="fixed bottom-24 right-5 sm:bottom-24 sm:right-6 z-[60] h-14 w-14 rounded-full bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)] grid place-items-center shadow-[0_8px_30px_rgba(0,231,255,0.4)] animate-pulse-ring"
            aria-label="AI Assistant">
            <Sparkles className="h-6 w-6" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            data-testid="ai-assistant-panel"
            className="fixed z-[60] glass rounded-2xl shadow-[0_18px_60px_rgba(0,0,0,0.6)] flex flex-col
              bottom-0 right-0 left-0 h-[80vh] rounded-b-none
              sm:bottom-5 sm:right-5 sm:left-auto sm:h-[560px] sm:w-[400px] sm:rounded-2xl">
            <div className="flex items-center justify-between p-4 border-b border-[color:var(--itw-border)]">
              <div className="flex items-center gap-3">
                <div className="relative h-9 w-9 rounded-full bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border-strong)] grid place-items-center">
                  <Sparkles className="h-4 w-4 text-[color:var(--itw-cyan)]" />
                  <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-[#2EF2A5] border-2 border-[color:var(--itw-surface)]" />
                </div>
                <div>
                  <div className="text-sm font-medium text-[color:var(--itw-text)]">{t('ai.title')}</div>
                  <div className="text-[10px] text-[color:var(--itw-text-3)]">{t('ai.status')}</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="ai-assistant-close-button" className="text-[color:var(--itw-text-2)] hover:text-[color:var(--itw-text)] transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === 'user'
                      ? 'bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)]'
                      : m.error ? 'bg-[#2a1620] text-[#FFB3C1] border border-[#FF5C7A]/30' : 'bg-[color:var(--itw-surface-2)] text-[color:var(--itw-text)] border border-[color:var(--itw-border)]'
                  }`}>
                    {m.content || (streaming && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : '')}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3 border-t border-[color:var(--itw-border)]">
              <div className="flex items-end gap-2">
                <textarea
                  value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey}
                  data-testid="ai-assistant-message-input"
                  placeholder={t('ai.placeholder')} rows={1}
                  className="flex-1 resize-none rounded-xl bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] px-3.5 py-2.5 text-sm text-[color:var(--itw-text)] placeholder:text-[color:var(--itw-text-3)] focus:outline-none focus:border-[color:var(--itw-border-strong)] max-h-28" />
                <button onClick={send} disabled={streaming || !input.trim()} data-testid="ai-assistant-send-button"
                  className="h-10 w-10 shrink-0 rounded-xl bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)] grid place-items-center disabled:opacity-40 transition-opacity">
                  {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
